/**
 * Single source of truth for the submission detail subsections.
 *
 * This array feeds BOTH the sidebar nav (which renders one link per
 * entry, grouped by `.group`) AND the "Save & Continue" flow (which
 * navigates to the entry at `currentIndex + 1`).
 *
 * Adding a new subsection = one entry in this array. Do NOT hardcode
 * the "next" target by id anywhere else — always go through
 * `getNextSection(currentId)`.
 *
 * Status:
 *   • `uw-information`, `schedule` and `legal` (powered by the
 *     existing Litigation Heatmap page) are wired to real pages.
 *   • Every other entry routes to the shared WorkInProgress shell —
 *     listed here so the sidebar still reflects the full information
 *     architecture while engineering catches up.
 *
 * The `legal` entry intentionally reuses the heat-map page: it's the
 * litigation heatmap with a new product-side label.
 */

export type SectionGroup = "submission" | "calculations" | "policy" | "files";

export interface SubmissionSection {
    id: string;
    label: string;
    /** Relative path under `/submissions/:id/`. */
    path: string;
    group: SectionGroup;
}

export const SUBMISSION_SECTIONS: readonly SubmissionSection[] = [
    { id: "uw-information", label: "UW Information", path: "uw-information", group: "submission" },
    { id: "schedule", label: "Schedule", path: "schedule", group: "submission" },
    { id: "loss-runs", label: "Loss Runs", path: "loss-runs", group: "submission" },
    { id: "regulatory", label: "Regulatory", path: "regulatory", group: "submission" },
    { id: "operational", label: "Operational", path: "operational", group: "submission" },
    { id: "legal", label: "Legal", path: "legal", group: "submission" },
    // Reputational — hidden for now: out of MVP scope (re-enable when the
    // section is built). Keep the entry so it's easy to restore.
    // { id: "reputational", label: "Reputational", path: "reputational", group: "submission" },
    { id: "premium-selection", label: "Premium Selection", path: "calculations/premium-selection", group: "calculations" },
    { id: "policy-details", label: "Policy Details", path: "policy/details", group: "policy" },
    { id: "endorsements", label: "Endorsements", path: "policy/endorsements", group: "policy" },
    { id: "subjectivities", label: "Subjectivities", path: "policy/subjectivities", group: "policy" },
    { id: "files", label: "Files", path: "files", group: "files" },
    // OR-1449 — shares the `files` group so it renders directly under Files
    // with no header of its own, matching the Figma.
    { id: "invoice", label: "Invoice", path: "invoice", group: "files" },
] as const;

/** Group labels rendered above each cluster of nav links. An empty
 *  string suppresses the header (used for `files`, which appears as a
 *  standalone entry below a divider per the Figma layout). */
export const GROUP_LABELS: Record<SectionGroup, string> = {
    submission: "Submission",
    calculations: "Calculations",
    policy: "Policy",
    files: "",
};

/** Find the section at position `currentIndex + 1`. Returns null on
 *  the last section. Consumers should fall through to navigating back
 *  to the submissions list when this returns null. */
export function getNextSection(currentId: string): SubmissionSection | null {
    const idx = SUBMISSION_SECTIONS.findIndex((s) => s.id === currentId);
    if (idx === -1) return null;
    return SUBMISSION_SECTIONS[idx + 1] ?? null;
}

export function getSectionById(id: string): SubmissionSection | null {
    return SUBMISSION_SECTIONS.find((s) => s.id === id) ?? null;
}
